import { FC } from 'react';
import { PreviousAttempts } from '../../../../models/PreviousAttempts';
import { AttemptInformation } from '../../../../models/AttemptInformation';
import { RatingDisplay } from '../../../../components/common/RatingDisplay';
import { CHALLENGE_PASSING_SCORE } from '../../constants';
import { RotateCcw } from 'lucide-react';
import { Button } from './ui/button';

interface ChallengeInnerNavProps {
  previousAttempts: PreviousAttempts[];
  attemptInformation: AttemptInformation | null;
  latestChallengeScore: number | null;
  onRetakeChallenge: () => void;
}

export const ChallengeInnerNav: FC<ChallengeInnerNavProps> = ({
  previousAttempts,
  attemptInformation,
  latestChallengeScore,
  onRetakeChallenge,
}) => {
  const totalAttempts = previousAttempts.length;
  const bestScore = totalAttempts ? Math.max(...previousAttempts.map(a => a.score)) : null;
  const displayScore = attemptInformation?.totalScore ?? latestChallengeScore;

  if (totalAttempts === 0 && !attemptInformation) {
    return (
      <div className="tw-p-2 tw-text-sm tw-text-neutral-700">
        Take the Skill Challenge to check your mastery. Your results will appear here.
      </div>
    );
  }

  return (
    <div className="tw-p-2 tw-flex tw-flex-col tw-gap-3">
      {displayScore !== null && (
        <div className="tw-flex tw-items-center tw-justify-between">
          <span className="tw-text-sm tw-font-atkinson" style={{ color: 'var(--muted-foreground)' }}>
            Last attempt
          </span>
          <RatingDisplay score={displayScore} options={{ showStars: true, showMessage: false, size: 'small' }} />
        </div>
      )}

      {bestScore !== null && bestScore !== displayScore && (
        <div className="tw-flex tw-items-center tw-justify-between">
          <span className="tw-text-sm tw-font-atkinson" style={{ color: 'var(--muted-foreground)' }}>
            Best score
          </span>
          <span
            className="tw-font-atkinson tw-font-bold tw-text-base"
            style={{ color: bestScore >= CHALLENGE_PASSING_SCORE ? '#16a34a' : 'var(--foreground)' }}
          >
            {bestScore}/10
          </span>
        </div>
      )}

      <div className="tw-flex tw-items-center tw-justify-between">
        <span className="tw-text-sm tw-font-atkinson" style={{ color: 'var(--muted-foreground)' }}>
          Total attempts
        </span>
        <span className="tw-font-atkinson tw-font-semibold tw-text-sm" style={{ color: 'var(--foreground)' }}>
          {totalAttempts}
        </span>
      </div>

      {totalAttempts > 0 && !attemptInformation && (
        <Button
          variant="outline"
          size="sm"
          onClick={onRetakeChallenge}
          className="tw-w-full tw-font-atkinson retake-challenge-pulse"
          style={{ gap: 'var(--spacing-2)', marginTop: 'var(--spacing-1)' }}
        >
          <RotateCcw style={{ width: '14px', height: '14px' }} />
          Retake Challenge
        </Button>
      )}
    </div>
  );
};
